import { dataLoader } from './dataLoader.js';
import { dataCache } from './dataCache.js';
import * as turf from '@turf/turf';

const REGION_DATA_URLS = ['/data/regions.geojson', '/data/regions.json'];

class RegionStore {
  constructor() {
    this.regions = null;
    this.planningAreas = null;
  }

  async init() {
    if (this.regions) return;

    const cached = dataCache.get('regions');
    if (cached) {
      this.regions = cached;
    } else {
      this.regions = await dataLoader.load(REGION_DATA_URLS);
      dataCache.set('regions', this.regions);
    }

    this.planningAreas = this._aggregateToPlanningAreas(this.regions);
  }

  findSubzoneForPoint(lngLat) {
    const point = turf.point(lngLat);
    for (const feature of this.regions.features) {
      if (turf.booleanPointInPolygon(point, feature.geometry)) {
        return this._extractRegionProps(feature);
      }
    }
    return null;
  }

  findPlanningAreaForPoint(lngLat) {
    const point = turf.point(lngLat);
    for (const feature of this.planningAreas.features) {
      if (turf.booleanPointInPolygon(point, feature.geometry)) {
        return this._extractPlanningAreaProps(feature);
      }
    }
    return null;
  }

  getAllRegions() {
    return this.regions;
  }

  getPlanningAreas() {
    return this.planningAreas;
  }

  _extractRegionProps(feature) {
    const p = feature.properties;
    return {
      id: p.SUBZONE_N?.toLowerCase().replace(/\s+/g, '_') || 'unknown',
      nameZh: p.SUBZONE_N || '未知区域',
      nameEn: p.SUBZONE_N || 'Unknown Area',
      planningArea: p.PLN_AREA_N,
      region: p.REGION_N,
      fengShuiProps: p.fengShui || null
    };
  }

  _extractPlanningAreaProps(feature) {
    const p = feature.properties;
    return {
      id: p.PLN_AREA_N?.toLowerCase().replace(/\s+/g, '_') || 'unknown',
      nameZh: p.PLN_AREA_N || '未知区域',
      nameEn: p.PLN_AREA_N || 'Unknown Area',
      region: p.REGION_N,
      fengShuiProps: p.fengShui || null
    };
  }

  _aggregateToPlanningAreas(regionsGeoJSON) {
    const groups = new Map();

    for (const feature of regionsGeoJSON.features) {
      const areaName = feature.properties.PLN_AREA_N;
      if (!groups.has(areaName)) {
        groups.set(areaName, []);
      }
      groups.get(areaName).push(feature);
    }

    const aggregatedFeatures = [];

    for (const [areaName, features] of groups) {
      if (features.length === 1) {
        const f = features[0];
        aggregatedFeatures.push({
          type: 'Feature',
          properties: {
            PLN_AREA_N: areaName,
            REGION_N: f.properties.REGION_N,
            fengShui: f.properties.fengShui
          },
          geometry: f.geometry
        });
      } else {
        const polygons = [];
        for (const f of features) {
          const geom = f.geometry;
          if (geom.type === 'Polygon') {
            polygons.push(geom.coordinates);
          } else if (geom.type === 'MultiPolygon') {
            for (const poly of geom.coordinates) {
              polygons.push(poly);
            }
          }
        }

        const dissolvedGeom = polygons.length > 0
          ? { type: 'MultiPolygon', coordinates: polygons }
          : features[0].geometry;

        aggregatedFeatures.push({
          type: 'Feature',
          properties: {
            PLN_AREA_N: areaName,
            REGION_N: features[0].properties.REGION_N,
            fengShui: features[0].properties.fengShui
          },
          geometry: dissolvedGeom
        });
      }
    }

    return {
      type: 'FeatureCollection',
      features: aggregatedFeatures
    };
  }
}

export const regionStore = new RegionStore();
